/**
 * A-05 invariant: monthly P&L acknowledgement requires a founder Ed25519 signature.
 * Founder pastes signature from FounderSignBar; sent as X-Founder-Signature on POST.
 */

import { useState } from 'react';
import { Navigate, Link, useParams, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { isOmegaAdmin } from '@/features/omega/adminMode';
import { Breadcrumbs } from '@/features/omega/Breadcrumbs';
import { apiBaseUrl, apiFetch } from '@/api/http';
import { getAccessToken } from '@/api/supabase';
import { fmtCents, fmtTs } from './AlphaOmegaDashboardPage';

// ── Types ────────────────────────────────────────────────────────────────────

interface MonthlyPlResponse {
  pl_id: string;
  company_id: string;
  period_start: number;
  period_end: number;
  revenue_cents: number;
  expenses_cents: number;
  profit_cents: number;
  acknowledged: boolean;
  ack_note: string | null;
  ack_at: number | null;
  created_at: string;
}

// ── Mutation ──────────────────────────────────────────────────────────────────

async function postAcknowledge(plId: string, sig: string, note?: string): Promise<void> {
  const token = await getAccessToken();
  const body = { note: note || null, nonce: crypto.randomUUID() };
  const resp = await fetch(`${apiBaseUrl()}/alpha-omega/monthly-pl/${plId}/acknowledge`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      'X-Founder-Signature': sig,
    },
    body: JSON.stringify(body),
  });
  if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
}

// ── Styles ─────────────────────────────────────────────────────────────────────

const inputStyle: React.CSSProperties = {
  background: 'rgba(255,255,255,0.06)',
  border: '1px solid var(--omega-rim, rgba(255,255,255,0.12))',
  borderRadius: 4, color: 'var(--omega-ink)', fontFamily: 'var(--omega-mono)',
  fontSize: '8.5pt', padding: '4px 8px', outline: 'none',
  width: '100%', boxSizing: 'border-box' as const,
};

// ── Sub-components ────────────────────────────────────────────────────────────

function PLRow({ label, value, highlight, positive }: { label: string; value: string; highlight?: boolean; positive?: boolean }) {
  const color = positive === true ? '#34d399' : positive === false ? '#f87171' : 'var(--omega-ink)';
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: '1fr 160px', gap: 8,
      padding: '10px 16px',
      borderBottom: '1px solid rgba(255,255,255,0.05)',
      background: highlight ? 'rgba(125,211,252,0.06)' : 'transparent',
    }}>
      <span style={{ fontSize: '9pt', color: 'var(--omega-ink)', fontWeight: highlight ? 700 : 400 }}>{label}</span>
      <span style={{ fontSize: '9pt', fontFamily: 'var(--omega-mono)', textAlign: 'right', color, fontWeight: highlight ? 700 : 400 }}>
        {value}
      </span>
    </div>
  );
}

function Toast({ msg, ok }: { msg: string; ok: boolean }) {
  return (
    <div style={{
      position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
      background: ok ? 'rgba(52,211,153,0.15)' : 'rgba(248,113,113,0.15)',
      border: `1px solid ${ok ? '#34d399' : '#f87171'}`,
      borderRadius: 6, color: ok ? '#34d399' : '#f87171',
      fontFamily: 'var(--omega-mono)', fontSize: '9pt',
      padding: '10px 20px', zIndex: 9999, direction: 'ltr',
    }}>
      {msg}
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function AlphaOmegaMonthlyPlDetailPage() {
  if (!isOmegaAdmin()) return <Navigate to="/" replace />;

  const { pl_id = '' } = useParams<{ pl_id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data, isPending, isError } = useQuery({
    queryKey: ['alpha-omega', 'monthly-pl', 'detail', pl_id],
    queryFn: () => apiFetch<MonthlyPlResponse>(`/alpha-omega/monthly-pl/${pl_id}`),
    refetchInterval: 30_000,
    enabled: !!pl_id,
  });

  const [sigInput, setSigInput] = useState('');
  const [noteInput, setNoteInput] = useState('');
  const [acting, setActing] = useState(false);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);

  function showToast(msg: string, ok: boolean) {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 3000);
  }

  async function handleAcknowledge() {
    if (!sigInput.trim()) return;
    setActing(true);
    try {
      await postAcknowledge(pl_id, sigInput.trim(), noteInput.trim() || undefined);
      await queryClient.invalidateQueries({ queryKey: ['alpha-omega', 'monthly-pl'] });
      showToast('CPA approved successfully', true);
      setTimeout(() => navigate('/omega/alpha-omega/monthly-pl'), 1500);
    } catch {
      showToast('Error — check signature and try again', false);
    } finally {
      setActing(false);
    }
  }

  const crumbLabel = data ? `${fmtTs(data.period_start)}` : pl_id.slice(0, 12);

  return (
    <div className="omega-shell" style={{ fontFamily: 'var(--omega-sans)', color: 'var(--omega-ink)', padding: '16px 20px' }}>
      <Breadcrumbs crumbs={[
        { label: 'OMEGA',       to: '/omega' },
        { label: 'Alpha-Omega', to: '/omega/alpha-omega' },
        { label: 'Monthly CPA',  to: '/omega/alpha-omega/monthly-pl' },
        { label: crumbLabel },
      ]} />

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, marginTop: 8 }}>
        <h1 style={{ margin: 0, fontSize: '12pt', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
          P&L — {data ? `${fmtTs(data.period_start)} – ${fmtTs(data.period_end)}` : 'Loading…'}
        </h1>
        {data && (
          <span style={{
            fontSize: '7.5pt', padding: '2px 8px',
            border: `1px solid ${data.acknowledged ? '#34d399' : '#fbbf24'}`,
            color: data.acknowledged ? '#34d399' : '#fbbf24',
            letterSpacing: '0.06em', textTransform: 'uppercase', fontWeight: 700, borderRadius: 3,
          }}>
            {data.acknowledged ? 'Approved' : 'Pending'}
          </span>
        )}
        <Link
          to="/omega/alpha-omega/monthly-pl"
          style={{
            marginLeft: 'auto', background: 'rgba(125,211,252,0.12)',
            border: '1px solid rgba(125,211,252,0.35)', borderRadius: 4,
            color: '#7dd3fc', fontFamily: 'var(--omega-mono)', fontSize: '8.5pt',
            fontWeight: 600, padding: '5px 12px', textDecoration: 'none',
          }}
        >
          ← Back to list
        </Link>
      </div>

      {isPending && <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '9pt' }}>Loading…</div>}
      {isError   && <div style={{ color: '#f87171', fontSize: '9pt' }}>Error loading data.</div>}

      {data && (
        <>
          <div style={{
            background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: 6, overflow: 'hidden', maxWidth: 560, marginBottom: 24,
          }}>
            <PLRow label="Revenue"  value={fmtCents(data.revenue_cents)}  positive />
            <PLRow label="Expenses"  value={`(${fmtCents(data.expenses_cents)})`} positive={false} />
            <PLRow
              label="Profit"
              value={fmtCents(data.profit_cents)}
              highlight
              positive={data.profit_cents >= 0}
            />
            {data.ack_note && (
              <div style={{ padding: '8px 16px', fontSize: '8pt', color: 'var(--omega-ink-subtle)', fontFamily: 'var(--omega-mono)', direction: 'ltr' }}>
                Note: {data.ack_note}
              </div>
            )}
            {data.ack_at && (
              <div style={{ padding: '4px 16px 8px', fontSize: '7.5pt', color: 'var(--omega-ink-subtle)', fontFamily: 'var(--omega-mono)' }}>
                Approved: {fmtTs(data.ack_at)}
              </div>
            )}
          </div>

          {/* A-05: acknowledge requires founder signature */}
          {!data.acknowledged && (
            <div style={{ padding: '18px 0 0', borderTop: '1px solid var(--omega-rim, rgba(255,255,255,0.1))' }}>
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: '8.5pt', color: 'var(--omega-ink-subtle)', letterSpacing: '0.06em', textTransform: 'uppercase', display: 'block', marginBottom: 6, direction: 'ltr' }}>
                  Signature (A-05 — FounderSignBar ← Cmd+Shift+S)
                </label>
                <input
                  type="text"
                  placeholder="Paste the signature here…"
                  value={sigInput}
                  onChange={e => setSigInput(e.target.value)}
                  style={{ ...inputStyle, direction: 'ltr' }}
                  dir="ltr"
                />
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: '8.5pt', color: 'var(--omega-ink-subtle)', letterSpacing: '0.06em', textTransform: 'uppercase', display: 'block', marginBottom: 6, direction: 'ltr' }}>
                  Note (optional)
                </label>
                <input
                  type="text"
                  placeholder="Add a note…"
                  value={noteInput}
                  onChange={e => setNoteInput(e.target.value)}
                  style={{ ...inputStyle, direction: 'ltr' }}
                  dir="ltr"
                />
              </div>
              <button
                disabled={!sigInput.trim() || acting}
                onClick={handleAcknowledge}
                style={{
                  background: (!sigInput.trim() || acting) ? 'rgba(52,211,153,0.06)' : 'rgba(52,211,153,0.12)',
                  border: '1px solid rgba(52,211,153,0.4)',
                  borderRadius: 4, color: '#34d399', fontFamily: 'var(--omega-mono)',
                  fontSize: '8.5pt', fontWeight: 600, padding: '6px 14px',
                  cursor: (!sigInput.trim() || acting) ? 'default' : 'pointer',
                  letterSpacing: '0.04em', opacity: (!sigInput.trim() || acting) ? 0.4 : 1,
                  direction: 'ltr',
                }}
              >
                Approve CPA (A-05)
              </button>
            </div>
          )}
        </>
      )}

      {toast && <Toast msg={toast.msg} ok={toast.ok} />}
    </div>
  );
}
